import React, { useMemo } from "react";

import moment from "moment";

import { ArrayUtils, formatNumber, useGetCarbonProgramByFarmId, useGetFieldPractices, useInView, useTillageAndSeedingHelpers, useTranslation } from "utils";

import { TILLAGE_DEPTH_RANGE_OPTIONS } from "consts";

import { PRACTICE_SLUG, PracticeFCPO } from "models";

import { Col, Icon, Row, SkeletonWrapper, Tooltip, Typography } from "components";

interface TillageAndSeedingTimelineProps {
  fieldId: string;
  farmId: string;
}

export const TillageAndSeedingTimeline = ({ fieldId, farmId }: TillageAndSeedingTimelineProps) => {
  const { getPracticeName, getDieselUsage, getCombinedPractices, sortPracticesByEndDate, getPracticeIconName } = useTillageAndSeedingHelpers();
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useTranslation("fieldTimeline");

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const requirePreciseTillageDepth = carbonProgram?.fieldsManagement.requirePreciseTillageDepth === true;

  // Resolve a stored tillageDepth to its display text, depending on whether the carbon program requires a precise value or a range bucket
  const getTillageDepthDisplay = (tillageDepth: number | undefined | null): string | null => {
    if (tillageDepth === undefined || tillageDepth === null) return null;
    if (requirePreciseTillageDepth) return `${formatNumber({ number: tillageDepth, decimalPlaces: 1 })} cm`;
    return TILLAGE_DEPTH_RANGE_OPTIONS.find((option) => option.value === tillageDepth)?.name ?? `${tillageDepth} cm`;
  };

  // Generate 7 years starting from current year - 5, including 2026
  const currentYear = moment().year();
  const years = Array.from({ length: 7 }, (_, index) => currentYear - 5 + index);

  // Get combined tillage and seeding practices
  const practices = getCombinedPractices(fieldPractices);

  // Group practices by year
  const practicesByYear = ArrayUtils.groupBy(practices, (practice) => practice?.year ?? "unknown");

  // Easter egg: Ludwig's Tractor - 1/100 chance for a tillage practice to show a tractor with special tooltip
  const ludwigTractorMap = useMemo(() => {
    const map = new Map<string, boolean>();
    practices.forEach((practice) => {
      if (practice.type === PRACTICE_SLUG.TILLAGES && practice.id) {
        const isLudwigsTractor = Math.random() < 1 / 50;
        if (isLudwigsTractor) {
          map.set(practice.id, true);
        }
      }
    });
    return map;
  }, [practices]);

  // Helper function to get detailed tooltip information
  const getPracticeTooltipInfo = (practice: PracticeFCPO): string => {
    // Easter egg: Check if this is Ludwig's tractor first - if so, return ONLY the special message
    const isLudwig = practice.id && ludwigTractorMap.has(practice.id);
    if (isLudwig) {
      return "🚜 *Tuk-tuk-tuk-tuk* ...Ahh, Ludwig arrives in his Legendary Tractor 🧙 and grants you +15 Tillage Mastery, +10 Diesel Efficiency";
    }

    // Normal tooltip for non-Ludwig tractors
    const name = getPracticeName(practice.type, practice.assignedPracticeId, t("fieldTimeline:cropCard.unknown"));
    const parts: string[] = [name];

    const dieselUsage = getDieselUsage(practice);
    if (dieselUsage !== null && dieselUsage !== undefined) {
      parts.push(`Diesel Usage: ${dieselUsage} L/ha`);
    }

    const tillageDepthDisplay = getTillageDepthDisplay(practice.tillageDepth);
    if (tillageDepthDisplay) {
      parts.push(`Tillage Depth: ${tillageDepthDisplay}`);
    }

    return parts.join("\n");
  };

  // Calculate total diesel usage for a year
  const calculateYearDieselUsage = (yearPractices: PracticeFCPO[]) =>
    yearPractices.reduce((total, practice) => {
      const dieselUsage = getDieselUsage(practice);
      return total + (dieselUsage ?? 0);
    }, 0);

  return (
    <SkeletonWrapper isLoading={isLoading} type="block" height="9rem">
      <div ref={ref} className="practice-timeline">
        <Row>
          {/* prelint-ignore-col */}
          {years.map((year) => {
            const yearPractices = practicesByYear?.[year] || [];
            const sortedPractices = sortPracticesByEndDate(yearPractices);
            const totalDieselUsage = calculateYearDieselUsage(yearPractices);
            return (
              <Col key={year} className="practice-timeline-year">
                <div className="practice-year-header">
                  <Typography variant="paragraph-s">{year}</Typography>
                </div>
                <div className="practice-year-content">
                  {sortedPractices.length === 0 ? (
                    <div className="practice-item practice-empty">No tillage/seeding</div>
                  ) : (
                    <>
                      {sortedPractices.map((practice) => {
                        // Easter egg: Use tractor icon if this is Ludwig's tractor
                        const isLudwigsTractor = practice.id && ludwigTractorMap.has(practice.id);
                        const iconName = isLudwigsTractor ? "tabler/tractor" : getPracticeIconName(practice.type);
                        const tooltipText = getPracticeTooltipInfo(practice);
                        // Use a unique key that includes Ludwig status to force tooltip re-render
                        const tooltipKey = `${practice.id}-${isLudwigsTractor ? "ludwig" : "normal"}`;

                        return (
                          <Tooltip key={tooltipKey} title={tooltipText} placement="top">
                            <div className={`practice-item ${practice.verificationStatus || "unknown"}`}>
                              <div className="fertilizer-content">
                                <div>
                                  <Icon iconName={iconName} fill="none" className="icon inline margin-right-25" size="s" />
                                  {getPracticeName(practice.type, practice.assignedPracticeId, t("fieldTimeline:cropCard.unknown"))}
                                </div>
                                {getTillageDepthDisplay(practice.tillageDepth) && (
                                  <div className="paragraph-xs">Depth: {getTillageDepthDisplay(practice.tillageDepth)}</div>
                                )}
                              </div>
                            </div>
                          </Tooltip>
                        );
                      })}
                      {totalDieselUsage > 0 && (
                        <div className="practice-nitrogen-total">
                          <Typography variant="paragraph-s">Σ Diesel: {totalDieselUsage.toFixed(2)} L/ha</Typography>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </SkeletonWrapper>
  );
};
